import React, { useRef } from "react";
import { motion, useScroll, useTransform, useSpring } from "framer-motion";
import { Box, Text, Container } from "@chakra-ui/react";

const AnimatedWord = ({ word, index, total, progress }) => {
  const start = (index / total) * 0.65;
  const end = start + 0.08;

  const smoothProgress = useSpring(progress, {
    stiffness: 100,
    damping: 30,
    restDelta: 0.001,
  });

  const opacity = useTransform(smoothProgress, [start, end], [0, 1]);
  const y = useTransform(smoothProgress, [start, end], [30, 0]);
  const scale = useTransform(smoothProgress, [start, end], [0.9, 1]);
  const blur = useTransform(smoothProgress, [start, end], ["6px", "0px"]);

  return (
    <motion.span
      style={{
        display: "inline-block",
        opacity,
        y,
        scale,
        filter: blur,
        willChange: "transform, opacity, filter",
      }}
    >
      <Text
        fontSize="clamp(1.5rem, 4vw, 3.5rem)"
        fontWeight={300}
        fontStyle="italic"
        color="white"
        display="inline-block"
      >
        {word}
      </Text>
    </motion.span>
  );
};

const ProgressDot = ({ index, total, progress }) => {
  const start = (index / total) * 0.65;
  const end = start + 0.08;

  const smoothProgress = useSpring(progress, {
    stiffness: 100,
    damping: 30,
    restDelta: 0.001,
  });

  const height = useTransform(smoothProgress, [start, end], ["0%", "100%"]);

  return (
    <Box
      width="2px"
      height="16px"
      bg="rgba(255, 255, 255, 0.1)"
      borderRadius="1px"
      position="relative"
      overflow="hidden"
    >
      <motion.div
        style={{
          height,
          position: "absolute",
          bottom: 0,
          left: 0,
          width: "100%",
          background: "#6778cb",
          borderRadius: "1px",
        }}
      />
    </Box>
  );
};

const MissionStatement = () => {
  const containerRef = useRef(null);

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end end"],
  });

  const smoothScrollProgress = useSpring(scrollYProgress, {
    stiffness: 80,
    damping: 30,
    restDelta: 0.001,
  });

  const quote =
    "Creating digital experiences that inspire, innovate, and make a lasting impact.";
  const words = quote.split(" ");

  const exitOpacity = useTransform(smoothScrollProgress, [0.8, 1], [1, 0]);
  const exitScale = useTransform(smoothScrollProgress, [0.8, 1], [1, 0.98]);
  const exitY = useTransform(smoothScrollProgress, [0.8, 1], [0, -30]);

  const openQuoteOpacity = useTransform(
    smoothScrollProgress,
    [0.02, 0.12],
    [0, 0.1],
  );
  const openQuoteX = useTransform(smoothScrollProgress, [0.02, 0.12], [-20, 0]);

  const closeQuoteOpacity = useTransform(
    smoothScrollProgress,
    [0.6, 0.7],
    [0, 0.1],
  );
  const closeQuoteX = useTransform(smoothScrollProgress, [0.6, 0.7], [20, 0]);
  // eslint-disable-next-line
  const indicatorOpacity = useTransform(
    smoothScrollProgress,
    [0, 0.04],
    [1, 0],
  );

  return (
    <Box
      ref={containerRef}
      position="relative"
      height="400vh"
      width="99vw"
      marginLeft="calc(-50vw + 50%)"
      marginRight="calc(-50vw + 50%)"
    >
      <Box
        position="sticky"
        top="0"
        height="100vh"
        width="100%"
        display="flex"
        alignItems="center"
        justifyContent="center"
        overflow="hidden"
      >
        <motion.div
          style={{
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            opacity: exitOpacity,
            scale: exitScale,
            y: exitY,
          }}
        >
          <Box
            position="absolute"
            top="50%"
            left="50%"
            transform="translate(-50%, -50%)"
            width="800px"
            height="800px"
            borderRadius="50%"
            bg="radial-gradient(circle, rgba(103, 120, 203, 0.03), transparent 60%)"
            pointerEvents="none"
          />

          <Container
            maxW="1200px"
            px={{ base: 5, md: 8, lg: 16 }}
            position="relative"
            zIndex={1}
          >
            <motion.div style={{ opacity: openQuoteOpacity, x: openQuoteX }}>
              <Text
                fontSize="6rem"
                color="#fff"
                lineHeight="1"
                mb="-2rem"
                textAlign="left"
              >
                "
              </Text>
            </motion.div>

            <Box
              display="flex"
              flexWrap="wrap"
              justifyContent="center"
              alignItems="center"
              gap={{ base: 2, md: 3, lg: 4 }}
            >
              {words.map((word, index) => (
                <AnimatedWord
                  key={index}
                  word={word}
                  index={index}
                  total={words.length}
                  progress={smoothScrollProgress}
                />
              ))}
            </Box>

            <motion.div style={{ opacity: closeQuoteOpacity, x: closeQuoteX }}>
              <Text
                fontSize="6rem"
                color="#fff"
                lineHeight="1"
                mt={{ base: "3rem", md: "-2rem" }}
                textAlign="right"
              >
                "
              </Text>
            </motion.div>
          </Container>

          <Box
            position="absolute"
            right={{ base: "1rem", md: "2rem" }}
            top="50%"
            transform="translateY(-50%)"
            display={{ base: "none", md: "block" }}
          >
            <Box display="flex" flexDirection="column" gap={2}>
              {words.map((_, index) => (
                <ProgressDot
                  key={index}
                  index={index}
                  total={words.length}
                  progress={smoothScrollProgress}
                />
              ))}
            </Box>
          </Box>
        </motion.div>
      </Box>
    </Box>
  );
};

export default MissionStatement;
